import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import SearchIcon from '../../assets/icons/search.svg?react';
import {
  PatientRoomControllerApi,
  PatientRoomPropertyControllerApi
} from '../../api/generated';
import type { PatientRoomPropertyResponse, PatientRoomResponse } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { FooterCell, Table } from '../UI';
import { DeleteRoomModal } from './DeleteRoomModal';
import { getRoomColumns } from './roomColumns';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import styles from './RoomsSettings.module.css';

const NEW_ROOM_ID = 'new' as const;
type EditingRoomId = string | null;

export const RoomsSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const locationId = locationIdParam ?? null;

  const [rooms, setRooms] = useState<PatientRoomResponse[]>([]);
  const [allRoomProperties, setAllRoomProperties] = useState<PatientRoomPropertyResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingRoomId, setEditingRoomId] = useState<EditingRoomId>(null);
  const [editingName, setEditingName] = useState('');
  const [openPickerRoomId, setOpenPickerRoomId] = useState<string | null>(null);
  const [roomToDelete, setRoomToDelete] = useState<PatientRoomResponse | null>(null);

  const refreshRooms = useCallback(
    async (locId: string) => {
      try {
        const config = createApiConfiguration();
        const roomApi = new PatientRoomControllerApi(config);
        const response = await roomApi.listPatientRooms({ locationId: locId });
        setRooms(response.data);
      } catch {
        dispatch(showError({ message: t('fetch-error') }));
      }
    },
    [dispatch, t]
  );

  useEffect(() => {
    if (!locationId) return;
    const init = async () => {
      try {
        const config = createApiConfiguration();
        const [roomsRes, propertiesRes] = await Promise.all([
          new PatientRoomControllerApi(config).listPatientRooms({ locationId }),
          new PatientRoomPropertyControllerApi(config).listPatientRoomProperties()
        ]);
        setRooms(roomsRes.data);
        setAllRoomProperties(propertiesRes.data);
      } catch {
        dispatch(showError({ message: t('fetch-error') }));
      }
    };
    void init();
  }, [locationId, dispatch, t]);

  const filteredRooms = useMemo(() => {
    if (!searchQuery) return rooms;
    const q = searchQuery.toLowerCase();
    return rooms.filter((r) => r.description.toLowerCase().includes(q));
  }, [rooms, searchQuery]);

  const effectiveEditingRoomId: EditingRoomId =
    editingRoomId === null || editingRoomId === NEW_ROOM_ID
      ? editingRoomId
      : filteredRooms.some((r) => r.id === editingRoomId)
        ? editingRoomId
        : null;

  const startEditName = useCallback(
    (roomId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      setEditingName(room?.description ?? '');
      setEditingRoomId(roomId);
      setOpenPickerRoomId(null);
    },
    [rooms]
  );

  const cancelEdit = useCallback(() => {
    setEditingRoomId(null);
    setEditingName('');
  }, []);

  const saveName = useCallback(
    async (roomId: string, name: string) => {
      if (!locationId) return;
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      try {
        const config = createApiConfiguration();
        const api = new PatientRoomControllerApi(config);
        await api.updatePatientRoom({
          locationId,
          patientRoomId: roomId,
          updatePatientRoomRequest: {
            description: name,
            patientRoomPropertyIds: room.patientRoomProperties.map((rp) => rp.id)
          }
        });
        setEditingRoomId(null);
        setEditingName('');
        await refreshRooms(locationId);
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, rooms, refreshRooms, dispatch, t]
  );

  const createRoom = useCallback(
    async (name: string) => {
      if (!locationId) return;
      try {
        const config = createApiConfiguration();
        const api = new PatientRoomControllerApi(config);
        await api.createPatientRoom({
          locationId,
          createPatientRoomRequest: { description: name, patientRoomPropertyIds: [] }
        });
        setEditingRoomId(null);
        await refreshRooms(locationId);
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, refreshRooms, dispatch, t]
  );

  const openPicker = useCallback((roomId: string) => {
    setOpenPickerRoomId(roomId);
  }, []);

  const closePicker = useCallback(() => {
    setOpenPickerRoomId(null);
  }, []);

  const addProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      if (!locationId) return;
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      try {
        const config = createApiConfiguration();
        const api = new PatientRoomControllerApi(config);
        await api.updatePatientRoom({
          locationId,
          patientRoomId: roomId,
          updatePatientRoomRequest: {
            description: room.description,
            patientRoomPropertyIds: [...room.patientRoomProperties.map((rp) => rp.id), propertyId]
          }
        });
        await refreshRooms(locationId);
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, rooms, refreshRooms, dispatch, t]
  );

  const removeProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      if (!locationId) return;
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      try {
        const config = createApiConfiguration();
        const api = new PatientRoomControllerApi(config);
        await api.updatePatientRoom({
          locationId,
          patientRoomId: roomId,
          updatePatientRoomRequest: {
            description: room.description,
            patientRoomPropertyIds: room.patientRoomProperties
              .map((rp) => rp.id)
              .filter((id) => id !== propertyId)
          }
        });
        await refreshRooms(locationId);
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, rooms, refreshRooms, dispatch, t]
  );

  const handleDeleted = useCallback(() => {
    setRoomToDelete(null);
    if (locationId) {
      void refreshRooms(locationId);
    }
  }, [locationId, refreshRooms]);

  const columns = useMemo(
    () =>
      getRoomColumns(t, {
        editingRoomId: effectiveEditingRoomId,
        editingName,
        openPickerRoomId,
        allRoomProperties,
        onStartEditName: startEditName,
        onNameChange: setEditingName,
        onSaveName: () => {
          if (!effectiveEditingRoomId || effectiveEditingRoomId === NEW_ROOM_ID) return;
          const trimmed = editingName.trim();
          if (trimmed.length === 0) return;
          void saveName(effectiveEditingRoomId, trimmed);
        },
        onCancelEdit: cancelEdit,
        onOpenPicker: openPicker,
        onClosePicker: closePicker,
        onAddProperty: (roomId, propertyId) => {
          void addProperty(roomId, propertyId);
        },
        onRemoveProperty: (roomId, propertyId) => {
          void removeProperty(roomId, propertyId);
        },
        onDelete: (room) => setRoomToDelete(room)
      }),
    [
      t,
      effectiveEditingRoomId,
      editingName,
      openPickerRoomId,
      allRoomProperties,
      startEditName,
      saveName,
      cancelEdit,
      openPicker,
      closePicker,
      addProperty,
      removeProperty
    ]
  );

  return (
    <section>
      <SectionHeader title={t('settings-rooms-header')}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{t('settings-search-room')}</label>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchQuery}
                placeholder={t('rooms-search-placeholder')}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      <Table
        columns={columns}
        data={filteredRooms}
        getRowKey={(room) => room.id}
        footer={
          <FooterCell
            isEditing={editingRoomId === NEW_ROOM_ID}
            addLabel={t('add-room')}
            onStartEdit={() => {
              setEditingRoomId(NEW_ROOM_ID);
              setOpenPickerRoomId(null);
            }}
            onSave={(name) => void createRoom(name)}
            onCancel={cancelEdit}
          />
        }
      />

      {roomToDelete && locationId && (
        <DeleteRoomModal
          room={roomToDelete}
          locationId={locationId}
          onClose={() => setRoomToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </section>
  );
};
